/**
 * Caméra 2D : déplacement, zoom, pincement, bornes dynamiques.
 *
 * Les mathématiques (conversions, bornage, zoom autour d'un point) sont des fonctions pures
 * exportées et testées ; seule la classe `Camera` touche au DOM. Cette séparation permet de
 * vérifier le comportement délicat — « le point sous le doigt ne bouge pas pendant le
 * pincement », « on ne peut pas perdre la ville hors de l'écran » — sans navigateur.
 *
 * Les bornes dépendent de la taille de la grille, qui grandit avec les achats de terrain
 * (§6) : `setBounds()` est donc appelée à chaque extension.
 */

import type { WorldBounds } from './isoEngine';
import { SYNTY_TILE } from './tileGeometry';

export interface CameraState {
  /** Point du monde affiché au centre du viewport. */
  readonly x: number;
  readonly y: number;
  readonly zoom: number;
}

export interface Viewport {
  readonly width: number;
  readonly height: number;
}

export interface ZoomLimits {
  readonly min: number;
  readonly max: number;
}

export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

export interface WorldPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Butées de zoom absolues, indépendantes de la grille et de l'écran.
 * Fournies par l'appelant depuis `config/balance` : la caméra n'importe aucune constante
 * de réglage, ce qui la garde testable et conforme au §19.
 */
export interface AbsoluteZoomLimits {
  readonly min: number;
  readonly max: number;
}

/** Marge laissée autour de la grille quand on calcule le zoom d'ajustement. */
const FIT_PADDING = 48;

/** Recul autorisé au-delà de l'ajustement exact, pour voir sa ville avec de l'air autour. */
const FIT_HEADROOM = 0.85;

/** En dessous de cette vitesse, l'inertie s'arrête. En unités monde par milliseconde. */
const INERTIA_STOP_SPEED = 0.02;

/** Fraction de vitesse conservée à chaque milliseconde d'inertie. */
const INERTIA_DECAY_PER_MS = 0.995;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function screenToWorld(
  screen: ScreenPoint,
  camera: CameraState,
  viewport: Viewport,
): WorldPoint {
  return {
    x: (screen.x - viewport.width / 2) / camera.zoom + camera.x,
    y: (screen.y - viewport.height / 2) / camera.zoom + camera.y,
  };
}

export function worldToScreen(
  world: WorldPoint,
  camera: CameraState,
  viewport: Viewport,
): ScreenPoint {
  return {
    x: (world.x - camera.x) * camera.zoom + viewport.width / 2,
    y: (world.y - camera.y) * camera.zoom + viewport.height / 2,
  };
}

/** Zoom auquel la grille entière tient dans le viewport, marge comprise. */
export function fitZoom(bounds: WorldBounds, viewport: Viewport): number {
  if (bounds.width <= 0 || bounds.height <= 0) {
    return 1;
  }
  const usableWidth = Math.max(1, viewport.width - FIT_PADDING);
  const usableHeight = Math.max(1, viewport.height - FIT_PADDING);
  return Math.min(usableWidth / bounds.width, usableHeight / bounds.height);
}

/**
 * Bornes de zoom pour une grille et un viewport donnés.
 *
 * Le plancher est le **plus permissif** des deux candidats : la butée absolue, et le zoom
 * d'ajustement avec un peu de recul. Prendre uniquement le second était un bug : sur un
 * large moniteur, `fit × 0,85` vaut davantage que le zoom de départ voulu, si bien que le
 * cadrage initial se faisait remonter de force et que la ville paraissait trop proche.
 * Prendre uniquement la butée absolue serait un bug symétrique : une très grande grille ne
 * tiendrait plus jamais entièrement à l'écran.
 */
export function zoomLimitsFor(
  bounds: WorldBounds,
  viewport: Viewport,
  absolute: AbsoluteZoomLimits,
): ZoomLimits {
  const fit = fitZoom(bounds, viewport);
  return { min: Math.min(fit * FIT_HEADROOM, absolute.min), max: absolute.max };
}

/**
 * Portion de l'écran MASQUÉE par l'interface, en pixels.
 *
 * Le dock en bas sous 1024 px, le panneau latéral au-delà, la barre de ressources en
 * haut : autant de bandes où un bâtiment est affiché mais inatteignable. La caméra doit
 * les connaître, sinon elle borne son déplacement sur un écran plus grand que celui dont
 * le joueur dispose réellement.
 *
 * ⚠️ Ces valeurs se MESURENT sur le DOM, elles ne se codent pas en dur : le dock change
 * de hauteur avec la langue, le panneau de largeur avec le viewport, et une constante
 * serait fausse au premier ajustement de mise en page (règle 14).
 */
export interface ViewportInsets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export const NO_INSETS: ViewportInsets = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * Empêche la ville de disparaître — et rien de plus.
 *
 * ══ DEUX versions fausses avant celle-ci, dans deux directions opposées ═════
 *
 * **Trop lâche, puis trop serrée.** La première disait « si la grille est plus petite que
 * le viewport, elle y est centrée » : la caméra était purement et simplement
 * **verrouillée**, et un bord de parcelle tombé sous le dock devenait inatteignable.
 *
 * La deuxième (0.55.0) a corrigé ça par « la zone libre reste DANS la ville ». Le bord
 * sous le dock était réglé, mais un autre verrou apparaissait, que Benoit a vu tout de
 * suite : **on ne pouvait aller ni à gauche ni en haut.** La caméra ne pouvait jamais
 * dépasser un bord de la ville, donc un angle restait collé au coin de l'écran, sous la
 * barre de ressources ou contre la bordure. Régler un débordement en interdisant le
 * débordement, c'est déplacer le problème d'un cran.
 *
 * ══ La règle, telle que Benoit l'a formulée ═════════════════════════════════
 *
 * « Il faut juste qu'on voie au minimum les angles. »
 *
 * C'est la BONNE contrainte, et c'est la seule : la ville doit toujours **chevaucher** la
 * zone libre d'au moins une tuile. À l'intérieur de ça, le joueur va où il veut — il peut
 * amener n'importe quel angle au milieu de l'écran, ce qu'aucune des deux versions
 * précédentes ne permettait.
 *
 * Un seul intervalle, jamais inversé, aucun cas particulier de taille : que la ville soit
 * plus grande ou plus petite que l'écran, la condition de chevauchement s'écrit pareil.
 * Les deux versions précédentes avaient chacune leur branche `if` — c'est cette branche
 * qui portait le verrou, les deux fois.
 *
 * La zone libre, elle, reste le viewport MOINS les bandes d'interface : c'est ce qui
 * garantit qu'un angle amené « à l'écran » ne se retrouve pas sous le dock, où il serait
 * visible et intouchable.
 */
export function clampCamera(
  camera: CameraState,
  bounds: WorldBounds,
  viewport: Viewport,
  insets: ViewportInsets = NO_INSETS,
): CameraState {
  return {
    x: clampAxis(
      camera.x,
      bounds.minX,
      bounds.maxX,
      viewport.width,
      insets.left,
      insets.right,
      camera.zoom,
      SYNTY_TILE.width,
    ),
    y: clampAxis(
      camera.y,
      bounds.minY,
      bounds.maxY,
      viewport.height,
      insets.top,
      insets.bottom,
      camera.zoom,
      SYNTY_TILE.height,
    ),
    zoom: camera.zoom,
  };
}

/**
 * Un axe du clamp. Séparé parce que les deux axes suivent la MÊME règle : la dupliquer
 * en ligne, c'est se garantir qu'un correctif n'en touchera qu'une moitié.
 *
 * `keep` est la longueur de ville qui doit rester dans la zone libre — « au minimum les
 * angles ». Elle vient de la taille d'une TUILE, mesurée, jamais d'un pourcentage
 * inventé : la garantie s'énonce alors dans l'unité du jeu — *au moins une tuile d'angle
 * reste visible et touchable* — plutôt qu'en fraction d'un écran dont on ne sait rien.
 */
function clampAxis(
  value: number,
  min: number,
  max: number,
  extent: number,
  before: number,
  after: number,
  zoom: number,
  keep: number,
): number {
  // Zone libre, en unités du monde. Jamais négative : une interface qui couvrirait tout
  // l'écran donnerait une zone de largeur nulle, pas une largeur inversée.
  const usable = Math.max(0, extent - before - after) / zoom;
  const half = usable / 2;
  // Le centre de la zone libre n'est pas celui du viewport dès qu'une seule bande existe.
  const offset = (before - after) / (2 * zoom);

  /*
    Chevauchement d'au moins `keep` entre [min, max] et la zone libre :

      bord droit de la zone libre  >= min + keep   →  value >= min + keep - half - offset
      bord gauche de la zone libre <= max - keep   →  value <= max - keep + half - offset

    `keep` est borné par la moitié de la ville : sur une ville plus petite qu'une tuile —
    ce qui n'arrive pas en jeu mais arrive dans un test — exiger une tuile de
    chevauchement inverserait l'intervalle.
  */
  const overlap = Math.min(keep, (max - min) / 2);
  const lo = min + overlap - half - offset;
  const hi = max - overlap + half - offset;

  return clamp(value, lo, hi);
}

/**
 * Zoome d'un facteur donné en gardant fixe le point du monde situé sous `anchor`.
 * C'est ce qui rend le pincement naturel : la tuile sous les doigts ne glisse pas.
 */
export function zoomAround(
  camera: CameraState,
  factor: number,
  anchor: ScreenPoint,
  viewport: Viewport,
  limits: ZoomLimits,
): CameraState {
  const nextZoom = clamp(camera.zoom * factor, limits.min, limits.max);

  // Zoom inchangé (butée atteinte) : ne pas déplacer la caméra pour autant.
  if (nextZoom === camera.zoom) {
    return camera;
  }

  const worldAnchor = screenToWorld(anchor, camera, viewport);

  return {
    x: worldAnchor.x - (anchor.x - viewport.width / 2) / nextZoom,
    y: worldAnchor.y - (anchor.y - viewport.height / 2) / nextZoom,
    zoom: nextZoom,
  };
}

interface PointerSample {
  readonly x: number;
  readonly y: number;
}

/**
 * Caméra interactive : glisser pour se déplacer, pincer ou molette pour zoomer,
 * inertie au relâchement.
 */
export class Camera {
  private state: CameraState;
  private bounds: WorldBounds;
  private viewport: Viewport;
  private limits: ZoomLimits;
  /** Bandes d'interface qui masquent l'écran. Fournies par l'appelant, jamais devinées. */
  private insets: ViewportInsets = NO_INSETS;

  private readonly pointers = new Map<number, PointerSample>();
  private pinchDistance = 0;
  private velocityX = 0;
  private velocityY = 0;
  private lastMoveTime = 0;
  private detachers: (() => void)[] = [];

  /**
   * Le glissement à UN doigt déplace-t-il la vue ?
   *
   * ══ Pourquoi ce réglage existe ══════════════════════════════════════════════
   *
   * Pendant une pose, le doigt appartient au BÂTIMENT, pas à la vue. C'est la
   * convention de tous les jeux de ville — on attrape ce qu'on pose et on le promène —
   * et sans elle le glisser-déposer est inécrivable : les deux gestes se disputeraient
   * le même doigt sur le même élément, exactement la cohabitation qui faisait déjà poser
   * un bâtiment en déplaçant la caméra.
   *
   * Le pincement à DEUX doigts, lui, n'est jamais suspendu : zoomer et recadrer pendant
   * qu'on cherche l'emplacement est précisément ce dont on a besoin.
   *
   * L'inertie est arrêtée au moment où l'on coupe : sans cela, une vue lancée juste
   * avant la sélection continuerait de glisser sous un fantôme censé être immobile.
   */
  private dragToPan = true;

  constructor(
    private readonly element: HTMLElement,
    bounds: WorldBounds,
    viewport: Viewport,
    private readonly absoluteLimits: AbsoluteZoomLimits,
  ) {
    this.bounds = bounds;
    this.viewport = viewport;
    this.limits = zoomLimitsFor(bounds, viewport, absoluteLimits);
    this.state = { x: bounds.centerX, y: bounds.centerY, zoom: this.limits.max };
    this.fit();
  }

  getState(): CameraState {
    return this.state;
  }

  getLimits(): ZoomLimits {
    return this.limits;
  }

  /** Recalcule les bornes après une extension de terrain, sans téléporter la caméra. */
  setBounds(bounds: WorldBounds): void {
    this.bounds = bounds;
    this.limits = zoomLimitsFor(bounds, this.viewport, this.absoluteLimits);
    this.applyState({
      ...this.state,
      zoom: clamp(this.state.zoom, this.limits.min, this.limits.max),
    });
  }

  /**
   * Déclare les bandes d'écran masquées par l'interface.
   *
   * Appelée au montage et à chaque changement de mise en page. On réapplique l'état
   * courant dans la foulée : sans cela, une caméra déjà posée contre une ancienne borne
   * y resterait jusqu'au prochain geste du joueur — c'est-à-dire jusqu'à ce qu'il se
   * demande pourquoi ça ne bouge pas.
   */
  setInsets(insets: ViewportInsets): void {
    this.insets = insets;
    this.applyState(this.state);
  }

  setViewport(viewport: Viewport): void {
    this.viewport = viewport;
    this.limits = zoomLimitsFor(this.bounds, viewport, this.absoluteLimits);
    this.applyState({
      ...this.state,
      zoom: clamp(this.state.zoom, this.limits.min, this.limits.max),
    });
  }

  /**
   * Zoom relatif autour du centre de l'écran, bornes comprises.
   *
   * Exposé pour les commandes d'interface : la molette et le pincement passent par le
   * même chemin interne, ce qui garantit qu'un bouton et un geste donnent exactement
   * le même résultat.
   */
  zoomBy(factor: number): void {
    this.stopInertia();
    this.applyState({
      ...this.state,
      zoom: clamp(this.state.zoom * factor, this.limits.min, this.limits.max),
    });
  }

  /** Cadre la grille entière et la centre. */
  fit(): void {
    this.stopInertia();
    this.applyState({
      x: this.bounds.centerX,
      y: this.bounds.centerY,
      zoom: clamp(fitZoom(this.bounds, this.viewport), this.limits.min, this.limits.max),
    });
  }

  /**
   * Centre la grille à un zoom **absolu**, indépendant de la taille du viewport.
   *
   * C'est ce que veut le cadrage de départ : une tuile doit faire un nombre de pixels
   * donné à l'écran (`CAMERA_BALANCE.initialTileScreenWidth`), qu'on soit sur téléphone ou
   * sur un grand écran. Ne pas dépendre du viewport rend aussi le cadrage insensible au
   * moment où la mise en page se stabilise — la source d'une classe entière de bugs.
   */
  /** Voir `dragToPan`. Le pincement à deux doigts reste actif dans tous les cas. */
  setDragToPan(enabled: boolean): void {
    if (this.dragToPan === enabled) return;
    this.dragToPan = enabled;
    // Une vue lancée juste avant la sélection continuerait de glisser sous un fantôme
    // que le joueur croit immobile.
    if (!enabled) this.stopInertia();
  }

  focusCenter(zoom: number): void {
    this.stopInertia();
    this.applyState({
      x: this.bounds.centerX,
      y: this.bounds.centerY,
      zoom: clamp(zoom, this.limits.min, this.limits.max),
    });
  }

  attach(): void {
    const onPointerDown = (event: PointerEvent): void => {
      /*
        `setPointerCapture` PEUT lever, et il ne doit jamais emporter le reste du geste.

        La spécification le fait échouer par `NotFoundError` quand aucun pointeur actif ne
        porte cet identifiant, et par `InvalidStateError` quand l'élément n'est pas
        rattaché au document. Placé en PREMIÈRE ligne du gestionnaire, un échec sautait
        tout ce qui suit — l'enregistrement du pointeur, l'arrêt de l'inertie, la mesure de
        pincement : la caméra ignorait alors qu'un doigt était posé et ne bougeait plus.

        La capture est un CONFORT — elle garde les `pointermove` quand le doigt sort du
        canvas — pas une condition de fonctionnement. Son échec ne justifie donc pas de
        perdre le geste. Trouvé par le harnais, qui simule un pincement par des
        `PointerEvent` synthétiques : leurs identifiants ne correspondent à aucun pointeur
        matériel, et l'exception remontait jusqu'à la console.
      */
      try {
        this.element.setPointerCapture(event.pointerId);
      } catch {
        // Sans capture : les `pointermove` cessent si le doigt quitte l'élément. Le
        // relâchement, lui, reste couvert par `pointercancel`.
      }
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.stopInertia();
      this.lastMoveTime = event.timeStamp;

      if (this.pointers.size === 2) {
        this.pinchDistance = this.currentPinchDistance();
      }
    };

    const onPointerMove = (event: PointerEvent): void => {
      const previous = this.pointers.get(event.pointerId);
      if (previous === undefined) {
        return;
      }

      const current = { x: event.clientX, y: event.clientY };
      this.pointers.set(event.pointerId, current);

      if (this.pointers.size === 1) {
        if (!this.dragToPan) return;
        this.panBy(current.x - previous.x, current.y - previous.y, event.timeStamp);
      } else if (this.pointers.size === 2) {
        this.pinch();
      }
    };

    const onPointerUp = (event: PointerEvent): void => {
      this.pointers.delete(event.pointerId);
      if (this.element.hasPointerCapture(event.pointerId)) {
        this.element.releasePointerCapture(event.pointerId);
      }
      // Repartir d'une base saine quand on passe de deux doigts à un seul.
      this.pinchDistance = this.pointers.size === 2 ? this.currentPinchDistance() : 0;
    };

    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      this.stopInertia();
      const factor = Math.exp(-event.deltaY * 0.0015);
      this.applyState(
        zoomAround(
          this.state,
          factor,
          this.localPoint(event.clientX, event.clientY),
          this.viewport,
          this.limits,
        ),
      );
    };

    this.element.addEventListener('pointerdown', onPointerDown);
    this.element.addEventListener('pointermove', onPointerMove);
    this.element.addEventListener('pointerup', onPointerUp);
    this.element.addEventListener('pointercancel', onPointerUp);
    this.element.addEventListener('wheel', onWheel, { passive: false });

    this.detachers = [
      (): void => {
        this.element.removeEventListener('pointerdown', onPointerDown);
      },
      (): void => {
        this.element.removeEventListener('pointermove', onPointerMove);
      },
      (): void => {
        this.element.removeEventListener('pointerup', onPointerUp);
      },
      (): void => {
        this.element.removeEventListener('pointercancel', onPointerUp);
      },
      (): void => {
        this.element.removeEventListener('wheel', onWheel);
      },
    ];
  }

  detach(): void {
    for (const detach of this.detachers) {
      detach();
    }
    this.detachers = [];
    this.pointers.clear();
  }

  /** Fait avancer l'inertie. À appeler une fois par frame avec le delta en millisecondes. */
  update(deltaMs: number): void {
    if (this.pointers.size > 0 || deltaMs <= 0) {
      return;
    }

    const speed = Math.hypot(this.velocityX, this.velocityY);
    if (speed < INERTIA_STOP_SPEED) {
      this.stopInertia();
      return;
    }

    this.applyState({
      x: this.state.x - this.velocityX * deltaMs,
      y: this.state.y - this.velocityY * deltaMs,
      zoom: this.state.zoom,
    });

    const decay = INERTIA_DECAY_PER_MS ** deltaMs;
    this.velocityX *= decay;
    this.velocityY *= decay;
  }

  private panBy(screenDeltaX: number, screenDeltaY: number, timeStamp: number): void {
    const worldDeltaX = screenDeltaX / this.state.zoom;
    const worldDeltaY = screenDeltaY / this.state.zoom;

    const elapsed = Math.max(1, timeStamp - this.lastMoveTime);
    this.lastMoveTime = timeStamp;
    this.velocityX = worldDeltaX / elapsed;
    this.velocityY = worldDeltaY / elapsed;

    this.applyState({
      x: this.state.x - worldDeltaX,
      y: this.state.y - worldDeltaY,
      zoom: this.state.zoom,
    });
  }

  private pinch(): void {
    const distance = this.currentPinchDistance();
    if (this.pinchDistance <= 0 || distance <= 0) {
      this.pinchDistance = distance;
      return;
    }

    const midpoint = this.pinchMidpoint();
    this.applyState(
      zoomAround(this.state, distance / this.pinchDistance, midpoint, this.viewport, this.limits),
    );
    this.pinchDistance = distance;
  }

  private currentPinchDistance(): number {
    const [first, second] = [...this.pointers.values()];
    if (first === undefined || second === undefined) {
      return 0;
    }
    return Math.hypot(second.x - first.x, second.y - first.y);
  }

  private pinchMidpoint(): ScreenPoint {
    const [first, second] = [...this.pointers.values()];
    if (first === undefined || second === undefined) {
      return { x: this.viewport.width / 2, y: this.viewport.height / 2 };
    }
    return this.localPoint((first.x + second.x) / 2, (first.y + second.y) / 2);
  }

  /** Convertit des coordonnées client en coordonnées locales au canvas. */
  private localPoint(clientX: number, clientY: number): ScreenPoint {
    const rect = this.element.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  private stopInertia(): void {
    this.velocityX = 0;
    this.velocityY = 0;
  }

  private applyState(next: CameraState): void {
    this.state = clampCamera(next, this.bounds, this.viewport, this.insets);
  }
}
